import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, clearToken, setCsrfToken } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Store user in sessionStorage
const getUserFromStorage = () => {
  try {
    const stored = sessionStorage.getItem('simlabdt_user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const setUserInStorage = (user) => {
  try {
    if (user) {
      sessionStorage.setItem('simlabdt_user', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('simlabdt_user');
    }
  } catch {}
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getUserFromStorage);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    // Cookie-only auth: always try to load user via httpOnly cookie
    try {
      const res = await authAPI.me();
      setUser(res.data);
      setUserInStorage(res.data);
    } catch (err) {
      // No valid session cookie — user is not logged in
      setUser(null);
      setUserInStorage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    loadUser();
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 10000);
    return () => clearTimeout(timeout);
  }, [loadUser]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    setUser(res.data.user);
    setUserInStorage(res.data.user);
    // CSRF token comes from X-CSRF-Token response header (set by middleware)
    const csrfFromHeader = res.headers?.['x-csrf-token'];
    if (csrfFromHeader) setCsrfToken(csrfFromHeader);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await authAPI.register({ email, password, name });
    setUser(res.data.user);
    setUserInStorage(res.data.user);
    const csrfFromHeader = res.headers?.['x-csrf-token'];
    if (csrfFromHeader) setCsrfToken(csrfFromHeader);
    return res.data;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {}
    setUser(null);
    setUserInStorage(null);
    clearToken();
  };

  const hasRole = (...roles) => user && roles.includes(user.role);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};
